/**
 * REST client for POST /resolve-content-name (microsoft-bi) — the Power BI content picker's
 * best-effort URL→name resolver (R3/R4).
 *
 * Contract — pinned in docs/specs/apps/microsoft-bi/36-content-picker-build-plan.md §2.
 *   request  { targetURL, encryptedAccessToken?, integrationKeyId? }
 *   200      ALWAYS. Exactly one of:
 *              { resolved: true, name, type: 'report'|'dashboard', workspaceName: string|null }
 *              { resolved: false }
 *            `workspaceName` is null for My Workspace or a failed best-effort lookup —
 *            indistinguishable by design. No other status code, no error channel: R4 is
 *            absolutist ("resolve fails, for any reason, show nothing at all"), and that
 *            absolutism starts HERE, not only on the server — literally everything else
 *            (network failure, a non-200, a malformed/unparseable body, a `resolved:true`
 *            shape missing `name`) collapses to `{resolved:false}`. This function must never
 *            throw.
 */

export type ResolvePowerBIContentNameResult =
  | { resolved: true; name: string; type: 'report' | 'dashboard'; workspaceName: string | null }
  | { resolved: false };

const NOT_RESOLVED: ResolvePowerBIContentNameResult = { resolved: false };

function withTrailingSlash(apiUrl: string): string {
  return apiUrl.endsWith('/') ? apiUrl : `${apiUrl}/`;
}

export function buildResolveContentNameUrl(args: { apiUrl: string }): string {
  return `${withTrailingSlash(args.apiUrl)}resolve-content-name`;
}

function authHeaders(token: string | null): Record<string, string> {
  return {
    'Content-Type': 'application/json',
    ...(token ? { Authorization: `Bearer ${token}` } : {}),
  };
}

interface RawBody {
  resolved?: boolean;
  name?: string;
  type?: string;
  workspaceName?: string | null;
}

export async function fetchResolveContentName(args: {
  apiUrl: string;
  token: string | null;
  targetURL: string;
  encryptedAccessToken?: string;
  integrationKeyId?: string;
}): Promise<ResolvePowerBIContentNameResult> {
  const body: Record<string, unknown> = { targetURL: args.targetURL };
  if (args.encryptedAccessToken) body.encryptedAccessToken = args.encryptedAccessToken;
  if (args.integrationKeyId) body.integrationKeyId = args.integrationKeyId;

  try {
    const res = await fetch(buildResolveContentNameUrl(args), {
      method: 'POST',
      headers: authHeaders(args.token),
      body: JSON.stringify(body),
    });

    if (!res.ok) return NOT_RESOLVED;

    let json: RawBody | undefined;
    try {
      json = await res.json();
    } catch {
      return NOT_RESOLVED;
    }

    if (
      json?.resolved === true &&
      typeof json.name === 'string' &&
      (json.type === 'report' || json.type === 'dashboard')
    ) {
      return {
        resolved: true,
        name: json.name,
        type: json.type,
        workspaceName: typeof json.workspaceName === 'string' ? json.workspaceName : null,
      };
    }
    return NOT_RESOLVED;
  } catch {
    // Network failure, aborted request, etc. — R4 starts at the client: never throw out of
    // this function, whatever the failure mode.
    return NOT_RESOLVED;
  }
}
